import { styled, useTh } from "leva/plugin";
import IconEasing from "./icons/IconEasing";
import IconDistribution from "./icons/IconDistribution";
import IconBounce from "./icons/IconBounce";
import IconElastic from "./icons/IconElastic";
import { useEffect, useState } from "react";

const MenuItem = styled("div", {
	display: "flex",
	alignItems: "center",
	gap: "8px",
	cursor: "pointer",
	padding: "8px 12px",
	borderRadius: "4px",
	transform: "translateY(-7px)",
	opacity: 0,
	transition:
		"transform 120ms cubic-bezier(0.36, 0.94, 0.53, 1), opacity 120ms cubic-bezier(0.36, 0.94, 0.53, 1)",
	"&:hover": {
		backgroundColor: "black",
	},
	"&.visible": {
		transform: "translateY(0px)",
		opacity: 1,
	},
});

const IconContainer = styled("div", {
	width: "16px",
	height: "16px",
	minWidth: "16px",
	minHeight: "16px",
	maxWidth: "16px",
	maxHeight: "16px",
});

interface MenuItemProps {
	onClick: () => void;
	currentDisplay: string;
	visible: boolean;
	index: number;
}

export const EasingMenuItem = ({
	onClick,
	currentDisplay,
	visible,
	index,
}: MenuItemProps) => {
	const accentColor = useTh("colors", "accent2");
	const mainColor = useTh("colors", "highlight3");
	return (
		<MenuItem
			style={{
				color: currentDisplay === "bezier" ? accentColor : mainColor,
				transitionDelay: `${index * 45}ms`,
			}}
			onClick={onClick}
			className={visible ? "visible" : ""}
		>
			<IconContainer style={{}}>
				<IconEasing />
			</IconContainer>
			<div>Easing</div>
		</MenuItem>
	);
};

export const DistributionMenuItem = ({
	onClick,
	currentDisplay,
	visible,
	index,
}: MenuItemProps) => {
	const accentColor = useTh("colors", "accent2");
	const mainColor = useTh("colors", "highlight3");
	return (
		<MenuItem
			style={{
				color: currentDisplay === "distribute" ? accentColor : mainColor,
				transitionDelay: `${index * 40}ms`,
			}}
			className={visible ? "visible" : ""}
			onClick={onClick}
		>
			<IconContainer>
				<IconDistribution />
			</IconContainer>
			<div>Distribute</div>
		</MenuItem>
	);
};

export const ElasticMenuItem = ({
	onClick,
	currentDisplay,
	visible,
	index,
}: MenuItemProps) => {
	const accentColor = useTh("colors", "accent2");
	const mainColor = useTh("colors", "highlight3");
	return (
		<MenuItem
			style={{
				color: currentDisplay === "elastic" ? accentColor : mainColor,
				transitionDelay: `${index * 30}ms`,
			}}
			onClick={onClick}
			className={visible ? "visible" : ""}
		>
			<IconContainer>
				<IconElastic />
			</IconContainer>
			<div>Elastic</div>
		</MenuItem>
	);
};

export const BounceMenuItem = ({
	onClick,
	currentDisplay,
	visible,
	index,
}: MenuItemProps) => {
	const accentColor = useTh("colors", "accent2");
	const mainColor = useTh("colors", "highlight3");
	return (
		<MenuItem
			style={{
				color: currentDisplay === "bounce" ? accentColor : mainColor,
				transitionDelay: `${index * 35}ms`,
			}}
			onClick={onClick}
			className={visible ? "visible" : ""}
		>
			<IconContainer>
				<IconBounce />
			</IconContainer>
			<div>Bounce</div>
		</MenuItem>
	);
};

function capitalizeFirstLetter(str: string): string {
	return str.charAt(0).toUpperCase() + str.slice(1);
}

// MenuItemButton.tsx
interface MenuItemButtonProps {
	onClick: () => void;
	label: string;
	currentDisplay: string;
	icon: React.ReactNode;
}

export const MenuItemButton: React.FC<MenuItemButtonProps> = ({
	onClick,
	label,
	currentDisplay,
	icon,
}) => {
	return (
		<div
			onClick={onClick}
			className={` px-2 py-[3px] w-fit rounded-md cursor-pointer outline-none ${
				currentDisplay === label
					? "bg-blue text-highlight3"
					: "bg-transparent hover:bg-elevation3 text-highlight2 hover:text-highlight2"
			}`}
		>
			<div className="hidden sm:flex text-sm">
				{label === "bezier" ? "Easing" : capitalizeFirstLetter(label)}
			</div>
			<div className="w-[18px] h-[18px] text-white sm:hidden flex">{icon}</div>
		</div>
	);
};
